import React, { useEffect, useState } from 'react';
import {
  IconCheck,
  IconCrown,
  IconBolt,
  IconLoader2
} from '@tabler/icons-react';
import { useAuth } from '../contexts/AuthContext';
import { billingAPI, authAPI } from '../services/api';

const TierCard = ({ title, price, features, cta, accent, current, popular }) => (
  <div className={`rounded-2xl border p-8 relative transition-all ${
    popular 
      ? 'border-primary-500 bg-gradient-to-b from-primary-500/5 to-white dark:to-dark-900' 
      : 'border-gray-200 dark:border-dark-800 bg-white dark:bg-dark-900'
  }`}>
    {popular && (
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-primary-500 to-yellow-400 text-black text-xs font-semibold px-4 py-1 rounded-full">
        Популярный
      </div>
    )}
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        {accent === 'gold' ? (
          <IconCrown className="w-5 h-5 text-amber-400" />
        ) : (
          <IconBolt className="w-5 h-5 text-primary-500" />
        )}
        <h3 className="text-gray-900 dark:text-white text-xl font-semibold">{title}</h3>
      </div>
      {current && (
        <span className="text-xs px-3 py-1 rounded-full bg-gray-100 dark:bg-dark-850 border border-gray-200 dark:border-dark-800 text-gray-600 dark:text-dark-400">Текущий план</span>
      )}
    </div>
    <div className="mb-6 pb-6 border-b border-gray-200 dark:border-dark-800">
      <span className="text-5xl font-bold text-gray-900 dark:text-white">{price}</span>
      {price !== '0 ₽' && <span className="text-gray-600 dark:text-dark-500 text-sm ml-1">/ месяц</span>}
    </div>
    <ul className="space-y-3 text-sm text-gray-600 dark:text-dark-400 mb-8">
      {features.map((f) => (
        <li key={f} className="flex items-center gap-3">
          <span className="text-primary-500 font-bold">✓</span>
          <span>{f}</span>
        </li>
      ))}
    </ul>
    {cta}
  </div>
);

const SubscribePage = () => {
  const { user, loading } = useAuth();
  const [plan, setPlan] = useState(user?.plan || 'free');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const sync = async () => {
      try {
        const me = await authAPI.checkAuth();
        if (me?.plan) setPlan(me.plan);
      } catch (e) {
        console.warn(e);
      }
    };
    sync();
  }, []);

  const handleSubscribe = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await billingAPI.createPayment();
      if (res.confirmation_url && res.payment_id) {
        localStorage.setItem('pending_payment_id', res.payment_id);
        window.location.href = res.confirmation_url;
      }
    } catch (e) {
      setError(e.message || 'Не удалось создать платёж');
    } finally {
      setBusy(false);
    }
  };

  const freeFeatures = [
    'До 10 туннелей',
    'До 5 доменов',
    'TCP / UDP протоколы',
    'Быстрый старт из CLI',
    'Community поддержка',
  ];

  const proFeatures = [
    'До 20 туннелей',
    'До 20 доменов',
    'Приоритетный доступ',
    'Быстрые обновления',
    'Приоритетная поддержка',
  ];

  return (
    <>
      <div className="min-h-screen bg-gray-50 dark:bg-dark-950 pt-28 pb-20 px-6 transition-colors">
        <div className="max-w-[1000px] mx-auto">
          <div className="text-center max-w-[600px] mx-auto mb-16">
            <p className="text-[13px] font-semibold text-primary-500 uppercase tracking-[2px] mb-4">
              Тарифы
            </p>
          <h1 className="text-[28px] sm:text-[32px] lg:text-[40px] font-bold text-gray-900 dark:text-white mb-4 tracking-[-0.5px]">Прозрачные цены</h1>
          <p className="text-lg text-gray-600 dark:text-dark-500">Начните бесплатно, масштабируйтесь без ограничений</p>
          {!loading && user && (
            <p className="text-primary-500 mt-4 text-sm">Текущий план: {plan === 'paid' ? 'PRO' : 'Free'}</p>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-[800px] mx-auto">
          <TierCard
            title="Free"
            price="0 ₽"
            features={freeFeatures}
            current={plan === 'free'}
            cta={<div className="text-gray-600 dark:text-dark-500 text-sm text-center">Активна по умолчанию</div>}
          />

          <TierCard
            title="PRO"
            price="179 ₽"
            features={proFeatures}
            current={plan === 'paid'}
            accent="gold"
            popular={true}
            cta={
              <button
                onClick={handleSubscribe}
                disabled={busy || plan === 'paid'}
                className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold transition-colors ${
                  plan === 'paid'
                    ? 'bg-gray-100 dark:bg-dark-850 border border-gray-200 dark:border-dark-800 text-gray-500 dark:text-dark-500 cursor-not-allowed'
                    : 'bg-primary-500 hover:bg-primary-600 text-white'
                }`}
              >
                {busy ? <IconLoader2 className="w-4 h-4 animate-spin" /> : <IconCrown className="w-4 h-4" />}
                <span>{plan === 'paid' ? 'Уже активен' : 'Оплатить через YooKassa'}</span>
              </button>
            }
          />
        </div>

        {error && <div className="mt-6 text-sm text-red-400 text-center">{error}</div>}
      </div>
    </div>
    </>
  );
};

export default SubscribePage;
